import { InputHTMLAttributes, useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { IFormFieldProps } from '../../../IFormFieldProps';
import MyUploaderHeader from '../MyUploaderHeader';
import Layout from './Layout';
import SendDocumentUseCase from '../../../../../domain/Document/UseCase/SendDocumentUseCase';
import FetchDocumentGateway from '../../../../../gateway/Document/FetchDocumentGateway';
import Document from '../../../../../domain/Document/Document';
import Converter from '../../../../util/Converter';
import LocalStorageGateway from '../../../../../gateway/Form/LocalStorageGateway';
import Receipt from '../../../../../domain/Receipt/Receipt';
import FieldErrorMessage from '../../messages/FieldErrorMessage';
import Loader from "../../../loader/Loader";
import ErrorModal from '../../../modal/ErrorModal';
import InvalidElement from '../../../../../domain/Folder/InvalidElementInterface';
import storage from '../../../../util/storage';
import { useFolderId } from '../../../../../infrastructure/hooks';
import { useToast } from '../../../toast/ToastContext';
import { incrementProvided } from '../../../../../store/element/receipt_counter';
import { useAppDispatch } from '../../../../../store/hook';

interface IProps extends InputHTMLAttributes<HTMLInputElement>, IFormFieldProps {
  classes?: string,
  label?: string,
  help?: string,
  id: string
  blockId: string
  typeId: string
  receiptsDefault: Receipt[] | null
  register: any
  clearErrors: any
  error: any
  readOnly?: boolean
  errorReceiptValidation?: any
  isRequired?: boolean
  updateReceipts?: (newReceipts: Receipt[]) => void
}

const Dropzone = ({ classes, label, help, id, blockId, typeId, receiptsDefault, register, clearErrors, error, readOnly, errorReceiptValidation, isRequired, updateReceipts }: IProps): JSX.Element => {
  const { t } = useTranslation()
  const { folderId } = useFolderId()
  const { addToast } = useToast()
  const dispatch = useAppDispatch();

  const [receipts, setReceipts] = useState<Receipt[] | null>(receiptsDefault)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorMessageNotif, setErrorMessageNotif] = useState<string | null>(null)
  const [isLoadingUpload, setLoadingUpload] = useState<boolean>(false)

  useEffect(() => {
    setReceipts(receiptsDefault)
  }, [receiptsDefault])

  const handleChangeInvalidElements = (invalidElements: InvalidElement[] | null, documents: Document[]) => {
    if (invalidElements) {
      let currentInvalidElements = invalidElements || [];

      if (currentInvalidElements.length > 0) {
        documents.forEach(document => {
          currentInvalidElements = removeInvalidElementById(
            currentInvalidElements,
            document.elementId
          );
        });
      }
      new LocalStorageGateway(folderId).saveInvalidElements(currentInvalidElements)
      window.dispatchEvent(new CustomEvent('invalidElementsUpdate'));
    }
  }

  const removeInvalidElementById = (invalidElements: InvalidElement[], targetElementId: string
  ): InvalidElement[] => {
    if (!invalidElements) return [];

    const updatedElements = invalidElements.filter(
      element => element.elementId !== targetElementId
    );

    return updatedElements;
  };

  const onDrop = useCallback(acceptedFiles => {
    clearErrors(id)
    setLoadingUpload(true)
    setErrorMessageNotif(null)

    const promises: Promise<any>[] = []
    acceptedFiles.map(file => {
      promises.push(new Converter().toBase64(file).then(base64 => {
        if (typeof base64 === 'string') {
          return new Document(typeId, id, base64, file.name)
        }
      }))
    })

    Promise.all(promises).then(documents => {
      if (documents.length > 0) {
        new SendDocumentUseCase(new FetchDocumentGateway(), new LocalStorageGateway(folderId)).execute(blockId, documents, folderId ?? undefined).then(response => {
          if (!response) {
            setErrorMessageNotif(t('error.error-upload'))
            return
          }
          const newReceipts = response?.filter(receipt => receipt.elementId === id)
          setReceipts((newReceipts) ?? null)
          if (updateReceipts) {
            updateReceipts((newReceipts) ?? null)
            if (isRequired && newReceipts && newReceipts.length <= 1) {
              console.log("increment provided", newReceipts)
              dispatch(incrementProvided())
            }
            addToast('success', t('common.file-downloaded'))
          }
          const invalidElements = storage.getInvalidElements(folderId)
          handleChangeInvalidElements(invalidElements, documents)
        })
      }
    })
      .finally(() => setLoadingUpload(false))

  }, [blockId, id, typeId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop
  })

  const receiptElement = receipts?.filter((receipt) => {
    return receipt.elementId === id
  })

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}

      <MyUploaderHeader classes={classes} label={label} help={help} />
      <div {...getRootProps()} className={`dzu-dropzone dzu-dropzone--receipt ${isDragActive ? 'dzu-dropzoneActive' : ''} ${isLoadingUpload ? 'dzu-dropzoneLoading' : ''}`}>
        <input {...getInputProps()} id={id + '-uploader'} />
        <input type={'hidden'} id={id} name={id} ref={register} data-skip-error={true} value={receiptElement && receiptElement?.length > 0 ? "1" : ""} />
        {isLoadingUpload ?
          <Loader />
          :
          <Layout />
        }
      </div>

      {error && !errorReceiptValidation && <FieldErrorMessage type={error.type} message={error.message} />}

      {errorMessageNotif &&
        <p className="notification notification--error u-pointer u-mts" onClick={() => setErrorMessageNotif(null)}>
          {errorMessageNotif}
        </p>
      }
    </>
  )
}

export default Dropzone;
